import { create } from "zustand";
import { persist } from "zustand/middleware";
import { createSafeLocalStorage } from "./persistStorage";
import { useUploadStore, type ExtendedUploadItem } from "./uploadStore";

export interface FloatingBarState {
  isVisible: boolean;
  isMinimized: boolean;
  currentItemId: string | null;
}

interface FloatingBarStoreState {
  floatingBar: FloatingBarState;
  showFloatingBar: (itemId: string) => void;
  hideFloatingBar: () => void;
  toggleFloatingBarMinimized: () => void;
  setFloatingBarMinimized: (minimized: boolean) => void;
}

export const FLOATING_BAR_STORAGE_KEY = "upload-floating-bar";

const INITIAL_FLOATING_BAR: FloatingBarState = {
  isVisible: false,
  isMinimized: false,
  currentItemId: null,
};

/**
 * Import·Download 진행 패널의 표시 상태.
 *
 * upload item과 분리된 store다. 패널을 띄우거나 닫는 것만으로 업로드 항목 전체가
 * 다시 직렬화되지 않아야 하기 때문이다. 이 store가 저장하는 값은 boolean 두 개와
 * item id 하나뿐이다.
 */
export const useFloatingBarStore = create<FloatingBarStoreState>()(
  persist(
    (set) => ({
      floatingBar: INITIAL_FLOATING_BAR,

      showFloatingBar: (itemId) =>
        set((state) => {
          const isSameItem = state.floatingBar.currentItemId === itemId;
          return {
            floatingBar: {
              ...state.floatingBar,
              isVisible: true,
              currentItemId: itemId,
              // Collapse preference applies only while the same transfer item is current.
              isMinimized: isSameItem ? state.floatingBar.isMinimized : false,
            },
          };
        }),

      hideFloatingBar: () =>
        set((state) => ({
          floatingBar: { ...state.floatingBar, isVisible: false, currentItemId: null },
        })),

      toggleFloatingBarMinimized: () =>
        set((state) => ({
          floatingBar: { ...state.floatingBar, isMinimized: !state.floatingBar.isMinimized },
        })),

      setFloatingBarMinimized: (minimized) =>
        set((state) => ({
          floatingBar: { ...state.floatingBar, isMinimized: minimized },
        })),
    }),
    {
      name: FLOATING_BAR_STORAGE_KEY,
      storage: createSafeLocalStorage<Pick<FloatingBarStoreState, "floatingBar">>(),
      partialize: (state) => ({ floatingBar: state.floatingBar }),
    },
  ),
);

export const floatingBarSelectors = {
  floatingBar: (state: FloatingBarStoreState): FloatingBarState => state.floatingBar,
};

/**
 * 진행 중이거나 사용자가 아직 닫지 않은 종료 항목은 패널에 남아야 한다(STUDIO-ASSET-003).
 * 완료된 download만 자동으로 사라진다.
 */
const RETAINED_STATUSES = new Set<ExtendedUploadItem["status"]>([
  "uploading",
  "uploaded",
  "converting",
  "optimizing",
  "processing",
  "bundling",
  "rendering",
  "finalizing",
  "paused",
  "downloading",
  "completed",
  "failed",
  "failed_capacity_post_conversion",
  "cancelled",
]);

export const isRetainedFloatingBarItem = (item: ExtendedUploadItem): boolean =>
  !item.editorImportAdmission?.dismissed &&
  RETAINED_STATUSES.has(item.status) &&
  !(item.kind === "download" && item.status === "completed");

/**
 * 한 항목이 끝나거나 취소돼도 다른 항목이 남아 있으면 패널을 숨기지 않는다.
 * 남은 항목이 있으면 그 항목을 대표로 유지하고, 없을 때만 숨긴다.
 */
export const hideFloatingBarUnlessRetained = (): void => {
  const retained = Object.values(useUploadStore.getState().items).find(
    isRetainedFloatingBarItem,
  );
  const store = useFloatingBarStore.getState();
  if (retained) {
    if (store.floatingBar.currentItemId !== retained.id) store.showFloatingBar(retained.id);
    return;
  }
  store.hideFloatingBar();
};

/** 패널이 대표로 보여 주는 upload item. 두 store를 함께 읽는 유일한 지점이다. */
export const useCurrentFloatingItem = (): ExtendedUploadItem | null => {
  const currentItemId = useFloatingBarStore((s) => s.floatingBar.currentItemId);
  return useUploadStore((s) => (currentItemId ? s.items[currentItemId] ?? null : null));
};
